/*!

=========================================================
* Argon Dashboard React - v1.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import Paper from '@material-ui/core/Paper';
import { DateTime } from 'luxon';
import qs from 'query-string';
import React, { useEffect, useState } from "react";
import { useQueryClient } from 'react-query';
import { Link, useHistory, useLocation } from 'react-router-dom';
import {
  Alert, Card, CardFooter, CardHeader, Col, Container, Row, Spinner, Table
} from "reactstrap";
import CustomPagination from '../components/CustomPagination';
import Header from "../components/Headers/Header";
// import Tooltip from '../components/Tooltip';
import { usePastExecutions } from '../hooks/usePastExecutions';
import { useQueryParams } from '../hooks/useQueryParams';
import { ExecutionInputType, UserPermissions } from '../types';
// import { secondsToTimeString } from '../utils/secondsToTimeString';
import { toCustomDateTimeString } from '../utils/toCustomDateTimeString';
import { toTitleCase } from '../utils/toTitleCase';
import { toTrimmedString } from '../utils/toTrimmedString';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { updateQueryParams } from '../utils/updateQueryParams';

const RESULTS_PER_PAGE = 10;

const HistoricalRuns: React.FC = () => {
  const query = useQueryParams()
  const { data: user } = useCurrentUser()

  const [pageTable, setPageTable] = useState(() => {
    let page = +(query.get('page') || 0)
    if (isNaN(page) || page <= 0) {
      return 1
    }
    return page
  })
  const [searchTerm, setSearchTerm] = useState(() => {
    const searchTerm = query.get('searchTerm')
    if (searchTerm) {
      return searchTerm
    }
    return ''
  })
  const [searchUsername, setSearchUsername] = useState('')
  const queryClient = useQueryClient()
  const location = useLocation()
  const router = useHistory()

  const {
    isError,
    isLoading,
    data,
    refetch,
  } = usePastExecutions(pageTable, RESULTS_PER_PAGE, searchTerm, searchUsername)

  useEffect(() => {
    handleExecSearch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTable])

  const handleExecSearch = () => {
    if (isLoading) {
      return
    }
    queryClient.removeQueries(["executions", "past"])
    refetch()
  }

  const handlePressEnter = () => {
    setPageTable(1)
    handleExecSearch()
    const q = qs.parse(location.search)
    q.searchTerm = searchTerm
    if (searchTerm.trim().length === 0) {
      delete q.searchTerm
    }
    // q.page = `${pageTable}`
    updateQueryParams(qs.stringify(q), router, location.pathname)
  }

  let content: JSX.Element | null = null

  if (isError) {
    content = (
      <Alert color="danger">
        Something went wrong!
      </Alert>
    );
  } else if (isLoading) {
    content = <Spinner />
  } else if (!data || data.data.length === 0) {
    content = (
      <Alert color="warning">
        No data available
      </Alert>
    );
  } else {
    content = (
      <>
        <Table className="align-items-center table-flush" responsive>
          <thead className="thead-light">
            <tr>
              <th scope="col">ID</th>
              <th scope="col">Question</th>
              <th scope="col"># Questions</th>
              <th scope="col">Input Type</th>
              <th scope="col">Executed By</th>
              <th scope="col">Date/Time of Execution</th>
            </tr>
          </thead>
          <tbody>
            {
              data.data.map((ex, i) => (
                <tr key={i}>
                  <th scope="row">
                    <Link to={`/admin/execution/${ex.id}`}>
                      {ex.id}
                    </Link>
                  </th>
                  <td scope="row" title={ex.question?.question || ''}>
                    {toTrimmedString(ex.question?.question || 'N/A')}
                  </td>
                  <td>{ex.numQues}</td>
                  <td>
                    {toTitleCase(ex.inputType)}
                    &nbsp;
                    ({ex.numWords} words{ex.inputType === ExecutionInputType.text ? '' : ', '} {ex.inputType === ExecutionInputType.file ? `${ex.numFiles} files` : (ex.inputType === ExecutionInputType.url ? `${ex.numUrls} urls` : '')})
                  </td>
                  <td>{ex.user.name}</td>
                  <td>{ex.createdAt ? toCustomDateTimeString(DateTime.fromISO(ex.createdAt)) : 'NA'}</td>
                </tr>
              ))
            }
          </tbody>
        </Table>
        <CardFooter className="py-4" style={{ textAlign: "center" }}>
          <nav style={{ display: "inline-block" }} aria-label="...">
            <CustomPagination
              activePage={pageTable}
              totalResults={data.total}
              resultsPerPage={RESULTS_PER_PAGE}
              onChange={setPageTable}
              label="Table navigation"

            />
          </nav>
        </CardFooter>
      </>
    )
  }
  return (
    <>
      <Header type='inf' />
      <Container className="mt--7" fluid>
        <Row className="mt-5">
          <Col className="mb-5 mb-xl-0">
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row>
                  <Col>
                    <h2>Past Executions</h2>
                  </Col>
                </Row>
                <Row className="align-items-center">
                  <div className='col-9'>
                    <div className="input-group-alternative input-group">
                      <div className="input-group-prepend">
                        <span onClick={handlePressEnter} className="input-group-text text-warning">
                          <i className="fas fa-search"></i>
                        </span>
                      </div>
                      <input
                        value={searchTerm}
                        onChange={e => {
                          setSearchTerm(e.target.value)
                        }}
                        onKeyDown={e => {
                          if (e.key.toLowerCase() === 'enter') {
                            handlePressEnter()
                          }
                        }}
                        placeholder="Search for any execution"
                        type="text"
                        className="form-control"
                      />
                    </div>
                  </div>
                  {
                    user?.roles.includes(UserPermissions.admin) &&
                    <div className="col-3">
                      <div className="input-group-alternative input-group">
                        <div className="input-group-prepend">
                          <span onClick={handlePressEnter} className="input-group-text text-warning">
                            <i className="fas fa-search"></i>
                          </span>
                        </div>
                        <input
                          value={searchUsername}
                          onChange={e => {
                            setSearchUsername(e.target.value)
                          }}
                          onKeyDown={e => {
                            if (e.key.toLowerCase() === 'enter') {
                              handlePressEnter()
                            }
                          }}
                          placeholder="Search by user's name"
                          type="text"
                          className="form-control"
                        />
                      </div>
                    </div>
                  }
                </Row>
              </CardHeader>
              <Paper className="px-4">
                {content}
              </Paper>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default HistoricalRuns;
